import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";

const STYLES: React.ComponentProps<typeof ReactDiffViewer>["styles"] = {
  variables: {
    light: {
      diffViewerBackground: "var(--bg-subtle)",
      addedBackground: "var(--score-a-bg)",
      removedBackground: "var(--score-c-bg)",
      codeFoldBackground: "var(--bg-subtle)",
    },
  },
  diffContainer: {
    fontSize: "12px",
    borderRadius: "var(--radius-sm)",
    overflow: "hidden",
    "& pre": {
      fontFamily: `"Helvetica", "Arial", "Hiragino Kaku Gothic Pro", "Yu Gothic", sans-serif`,
      whiteSpace: "pre",
    },
    "& td": { whiteSpace: "nowrap" },
  },
};

interface ChangeRequestItemDiffProps {
  /** Current DB value. */
  oldValue: unknown;
  /** Proposed value. */
  newValue: unknown;
  /** "outline" trims surrounding context; "detail" shows 3 lines around each change. */
  viewMode: "outline" | "detail";
}

/**
 * DiffMethod.JSON stringifies each value and calls .split on it, so an `undefined`
 * value throws "reading 'split'" (DEWEY-7P). JSON.stringify drops undefined keys
 * anyway, so pruning first is crash-safe without changing the rendered diff.
 */
export function pruneUndefined(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((v) => (v === undefined ? null : pruneUndefined(v)));
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, pruneUndefined(v)]),
    );
  }
  return value;
}

/** Renders an old-vs-new JSON diff for one change-request item (V1 ChangeRequestItemPropertiesDiff parity). */
export function ChangeRequestItemDiff({ oldValue, newValue, viewMode }: ChangeRequestItemDiffProps) {
  return (
    <ReactDiffViewer
      disableWordDiff
      hideLineNumbers
      splitView={false}
      extraLinesSurroundingDiff={viewMode === "outline" ? 0 : 3}
      compareMethod={DiffMethod.JSON}
      newValue={pruneUndefined(newValue) as Record<string, unknown>}
      oldValue={pruneUndefined(oldValue) as Record<string, unknown>}
      styles={STYLES}
    />
  );
}
